// AnonUidStore — Phase 8 Task 8-5 (定着率メトリクス計装) 用の anonymous user
// identifier persistence in localStorage.
//
// Purpose:
// - 全 `room.join` 送信時に乗せる `clientAnonId` (UUID v4) を **永続的に**
//   発行 / 取得する. analytics layer で `OEKAKI_ANALYTICS_SALT` を使った
//   HMAC-SHA256 fingerprint に変換され、DAU / D1-D7-D30 リピート率の
//   cohort 分析に使われる. raw 値はサーバ DB / log / metric に書かれない.
//   (analytics.md §1 / §3.1 / §8、protocol.md `room.join.clientAnonId`)
//
// **Why singleton (not per-room)**: 「同一ユーザの識別」が目的なので、ルーム
// 横断で 1 ID を共有する. AdminEmailStore と同流儀.
//
// **Why "lazy generation on first read" instead of "generate on app boot"**:
// app boot 経路 (HomePage / ReJoinScreen 等) は複数あり、Storage 不可な環境
// では発行を諦める前提なので、初回 `join()` (= ストレージが必要になった瞬間)
// に lazy create する方が分岐が少ない.
//
// **Storage 不可な環境** (Safari private browsing 厳格モード / SSR / cookie
// ブロック設定 / `globalThis.crypto.randomUUID` 不在の古いブラウザ等) の挙動:
// - getOrCreate() は `null` を返す.
// - **AdminTokenStore とは違い、in-memory fallback は使わない**. anonUid は
//   タブ間 / リロード間で同一であって初めて意味がある (= retention 計測の
//   key として機能する) ため、ページリロードで毎回別 UUID を発行するのは
//   protocol を「壊さない」(送らない) よりも analytics 的に害がある
//   (= D1 retention が常時 0 % にバイアスされる). null 返却 → 呼び出し側が
//   `room.join.clientAnonId` を **送らない** ことで対応する (server 側は
//   不在時に session 限定 ID へ fallback する設計、analytics.md §3.1).
//
// Tests: AnonUidStore.test.ts (storage-injection style、AdminTokenStore.test.ts と
// 同じ流儀).

const KEY = 'oekaki:anonUid';

/**
 * Minimal Storage shape this module uses (mirrors AdminTokenStore.ts).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Minimal subset of `Crypto` we need (just `randomUUID`). Lets tests inject
 * a deterministic generator without touching `globalThis.crypto`.
 */
export interface UuidSource {
  randomUUID(): string;
}

export interface AnonUidStore {
  /**
   * Return the persisted anonymous UUID. Generates and persists one on the
   * first call if absent. Returns `null` when:
   *  - the underlying storage is unusable (probe failed / `setItem` throws), OR
   *  - no UUID source is available (`crypto.randomUUID` missing).
   * Callers omit `clientAnonId` from `room.join` in the null case.
   */
  getOrCreate(): string | null;
  /** Look up the persisted UUID without creating. Returns `null` if absent. */
  peek(): string | null;
  /** Forget the persisted UUID. Idempotent. */
  clear(): void;
}

/**
 * Probe `globalThis.localStorage` and return it if usable, else `null`.
 *
 * Mirrors AdminTokenStore's probe: Safari private browsing / QuotaExceeded
 * surface as exceptions on `setItem`. We test by writing then deleting a
 * sentinel key.
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/**
 * Probe `globalThis.crypto.randomUUID`. Returns the source if present,
 * `null` otherwise (very old browsers / non-secure context where Crypto
 * API is partially gated).
 */
const probeUuidSource = (): UuidSource | null => {
  const c = globalThis.crypto;
  if (typeof c === 'undefined') return null;
  if (typeof (c as Partial<Crypto>).randomUUID !== 'function') return null;
  return { randomUUID: () => c.randomUUID() };
};

export interface CreateAnonUidStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * return `null` if unavailable (= getOrCreate returns null).
   *
   * Pass `null` to **force** the no-storage path (= getOrCreate always
   * returns null). Tests use this to verify the protocol-doesn't-break path.
   */
  storage?: StorageLike | null;
  /**
   * Override the UUID source. Default: probe `globalThis.crypto.randomUUID`.
   * Pass `null` to force the no-uuid-source path.
   */
  uuid?: UuidSource | null;
}

/**
 * Construct an `AnonUidStore`. Returns `null` from `getOrCreate()` whenever
 * either storage or the UUID source is unavailable, so callers can simply
 * spread `clientAnonId` conditionally into `room.join`.
 */
export const createAnonUidStore = (opts: CreateAnonUidStoreOptions = {}): AnonUidStore => {
  // `null` 明示は「強制 no-op」(= テストで「Storage 不可環境」を表現), `undefined`
  // は auto-probe. AdminTokenStore とは違い、no-op 時は in-memory fallback を
  // 使わない (= ファイル先頭コメント参照).
  const explicitStorage = opts.storage;
  const storage = explicitStorage === null ? null : (explicitStorage ?? probeBrowserStorage());

  const explicitUuid = opts.uuid;
  const uuid = explicitUuid === null ? null : (explicitUuid ?? probeUuidSource());

  return {
    getOrCreate() {
      if (storage === null) return null;
      try {
        const existing = storage.getItem(KEY);
        if (existing !== null && existing.length > 0) return existing;
      } catch {
        return null;
      }
      // 不在 — 新規発行. UUID source が無い場合は諦める (= 呼び出し側で
      // clientAnonId を送らない).
      if (uuid === null) return null;
      let next: string;
      try {
        next = uuid.randomUUID();
      } catch {
        return null;
      }
      try {
        storage.setItem(KEY, next);
      } catch {
        // Quota / 書き込み不可 — 永続化できないなら send しない方が analytics
        // 的に正しい (= リロード毎に別 UUID を送ると D1 retention がゼロに
        // バイアスされる).
        return null;
      }
      return next;
    },
    peek() {
      if (storage === null) return null;
      try {
        const v = storage.getItem(KEY);
        return v !== null && v.length > 0 ? v : null;
      } catch {
        return null;
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(KEY);
      } catch {
        // ignore
      }
    },
  };
};
